// src/pages/Emergencias.js
import React, { useState, useEffect } from 'react';
import { 
  Download, 
  Plus, 
  Eye, 
  Edit,
  Filter,
  Search,
  Clock,
  Activity,
  CheckCircle,
  ArrowRight,
  PlayCircle,
  MousePointerClick,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { emergenciasAPI } from '../services/apiService';
import ModalEmergencia from '../components/emergencias/ModalEmergencia';
import ModalVerEmergencia from '../components/emergencias/ModalVerEmergencia';
import ModalRegistrarEjecucion from '../components/emergencias/ModalRegistrarEjecucion';

// Estilos CSS para animaciones
const styles = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
`;

// Agregar estilos al documento si no existen
if (typeof document !== 'undefined' && !document.getElementById('emergencias-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'emergencias-styles';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// Función para formatear fechas
const formatearFecha = (fechaString) => {
  if (!fechaString) return 'N/A';
  
  try {
    if (fechaString.includes('-') && fechaString.split('-')[0].length <= 2) {
      const [dia, mes, año] = fechaString.split('-');
      const fecha = new Date(año, mes - 1, dia);
      return fecha.toLocaleDateString('es-HN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    }
    
    const fecha = new Date(fechaString);
    if (!isNaN(fecha.getTime())) {
      return fecha.toLocaleDateString('es-HN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
    }
    
    return fechaString;
  } catch (error) {
    return fechaString;
  }
};

const Emergencias = () => {
  const [emergencias, setEmergencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Estados para los modales
  const [modalRegistro, setModalRegistro] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalVer, setModalVer] = useState(false);
  const [modalEjecucion, setModalEjecucion] = useState(false);
  const [emergenciaSeleccionada, setEmergenciaSeleccionada] = useState(null);

  useEffect(() => {
    loadEmergencias();
  }, []);

  const loadEmergencias = async () => {
    try {
      setLoading(true);
      const response = await emergenciasAPI.getAll();
      
      console.log('📥 Respuesta de emergencias:', response);
      
      // Manejar diferentes formatos de respuesta
      let data;
      if (Array.isArray(response)) {
        // Si es array directo
        data = response;
      } else if (response?.success && response?.data) {
        // Si tiene estructura {success, data}
        data = response.data;
      } else if (response && typeof response === 'object' && !response.success) {
        // Si es un objeto sin success, asumir que es el data directamente
        data = response;
      } else {
        // Fallback
        data = [];
      }
      
      if (Array.isArray(data)) {
        setEmergencias(data);
        setError(null);
      } else {
        console.error('❌ Formato de respuesta inesperado:', data);
        setError('Error: Formato de datos inválido');
        setEmergencias([]);
      }
    } catch (err) {
      console.error('❌ Error cargando emergencias:', err);
      setError(err.response?.data?.message || err.message || 'No se pudo conectar con el servidor');
      setEmergencias([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstado = async (emergencia, nuevoEstado) => {
    try {
      await emergenciasAPI.updateBatch([{
        Id: emergencia.Id,
        Estado: nuevoEstado
      }]);
      loadEmergencias();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      alert('Error al actualizar el estado');
    }
  };

  const handleRegistrarEjecucion = (emergencia) => {
    setEmergenciaSeleccionada(emergencia);
    setModalEjecucion(true);
  };

  const handleVer = (emergencia) => {
    setEmergenciaSeleccionada(emergencia);
    setModalVer(true);
  };

  const handleEditar = (emergencia) => {
    setEmergenciaSeleccionada(emergencia);
    setModalEditar(true);
  };

  const handleNuevo = () => {
    setEmergenciaSeleccionada(null);
    setModalRegistro(true);
  };

  const handleSuccess = () => {
    loadEmergencias();
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Filtrar emergencias por búsqueda
  const filteredEmergencias = emergencias.filter(em => 
    em.NombreMunicipio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    em.NombreTipoEmergencia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    em.NombreCreador?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    em.NombreFenomeno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    em.Id?.toString().includes(searchTerm)
  );

  // Cálculos de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEmergencias.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmergencias.length / itemsPerPage);

  // Funciones de paginación
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    setExpandedRows([]); // Cerrar filas expandidas al cambiar de página
  };

  const changeItemsPerPage = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Volver a la primera página
    setExpandedRows([]); // Cerrar filas expandidas
  };

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const delta = 2; // Número de páginas a mostrar a cada lado de la actual
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c8a88c] mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando emergencias...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button 
          onClick={loadEmergencias}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Emergencias</h1>
          <p className="text-gray-500 mt-1">Registros de emergencias</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleNuevo}
            className="flex items-center gap-2 px-4 py-2 bg-[#c8a88c] text-white rounded-lg font-medium hover:bg-[#b8987c] shadow-md transition-colors"
          >
            <Plus className="w-5 h-5" />
            Registrar
          </button>
        </div>
      </div>

      {/* Filtros y Búsqueda */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por ID, municipio, tipo, creador o evento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Instrucción para el usuario */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
        <MousePointerClick className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> Haz clic sobre cualquier fila para ver las acciones disponibles. Puedes tener múltiples emergencias abiertas a la vez.
        </p>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Municipio</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Evento</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Creado por</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Inversión</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm ? 'No se encontraron emergencias que coincidan con tu búsqueda' : 'No hay emergencias registradas'}
                  </td>
                </tr>
              ) : (
                currentItems.map((emergencia) => (
                  <React.Fragment key={emergencia.Id}>
                    <tr 
                      onClick={() => toggleRow(emergencia.Id)}
                      onMouseEnter={() => setHoveredRow(emergencia.Id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      className={`
                        transition-all cursor-pointer
                        ${expandedRows.includes(emergencia.Id) 
                          ? 'bg-gray-50 border-l-4 border-l-[#c8a88c]' 
                          : 'hover:bg-gray-50/50'
                        }
                      `}
                    >
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="font-medium">{formatearFecha(emergencia.Fecha)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {emergencia.NombreMunicipio || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {emergencia.ZonaCafetalera || 'Sin zona'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">
                          {emergencia.NombreTipoEmergencia || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {emergencia.NombreTipoRed || 'Sin tipo'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <EstadoBadge estado={emergencia.Estado} />
                          {emergencia.Estado !== 'Atendido' && (
                            <div className="relative group">
                              <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#c8a88c] rounded-full animate-pulse"></div>
                              <div className="text-xs text-[#c8a88c] font-semibold opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                Clic para ver siguiente paso
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">
                          {emergencia.NombreFenomeno || 'Sin evento'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">
                          {emergencia.NombreCreador || 'N/A'}
                        </div>
                       
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">
                          L {emergencia.InversionProyectada?.toLocaleString('es-HN') || '0'}
                        </div>
                      </td>
                    </tr>

                    {/* Fila Expandida con Acciones */}
                    {expandedRows.includes(emergencia.Id) && (
                      <tr className="bg-gradient-to-r from-gray-50 via-white to-gray-50 border-l-4 border-l-[#c8a88c]">
                        <td colSpan="7" className="px-6 py-6">
                          <div className="animate-fadeIn">
                            <AccionesEmergencia 
                              emergencia={emergencia}
                              onCambiarEstado={handleCambiarEstado}
                              onRegistrarEjecucion={handleRegistrarEjecucion}
                              onVer={handleVer}
                              onEditar={handleEditar}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Controles de Paginación */}
        {filteredEmergencias.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Selector de items por página */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Mostrar</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => changeItemsPerPage(Number(e.target.value))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent focus:outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">registros</span>
              </div>

              {/* Info de registros */}
              <div className="text-sm text-gray-600">
                Mostrando <span className="font-semibold">{indexOfFirstItem + 1}</span> a{' '}
                <span className="font-semibold">{Math.min(indexOfLastItem, filteredEmergencias.length)}</span> de{' '}
                <span className="font-semibold">{filteredEmergencias.length}</span> emergencias
              </div>

              {/* Botones de navegación */}
              <div className="flex items-center gap-1">
                {/* Primera página */}
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Primera página"
                >
                  <ChevronsLeft className="w-5 h-5" />
                </button>

                {/* Página anterior */}
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Página anterior"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                {/* Números de página */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageNumber, index) => (
                    pageNumber === '...' ? (
                      <span key={`dots-${index}`} className="px-3 py-1.5 text-gray-500">
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNumber}
                        onClick={() => goToPage(pageNumber)}
                        className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
                          currentPage === pageNumber
                            ? 'bg-[#c8a88c] text-white'
                            : 'hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    )
                  ))}
                </div>

                {/* Página siguiente */}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Página siguiente"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Última página */}
                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Última página"
                >
                  <ChevronsRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info adicional */}
      {expandedRows.length > 0 && (
        <div className="text-sm text-[#c8a88c] font-medium text-center">
          {expandedRows.length} {expandedRows.length === 1 ? 'fila expandida' : 'filas expandidas'}
        </div>
      )}

      {/* Modales */}
      <ModalEmergencia
        isOpen={modalRegistro}
        onClose={() => setModalRegistro(false)}
        onSuccess={handleSuccess}
      />

      <ModalEmergencia
        isOpen={modalEditar}
        onClose={() => setModalEditar(false)}
        emergencia={emergenciaSeleccionada}
        onSuccess={handleSuccess}
      />

      <ModalVerEmergencia
        isOpen={modalVer}
        onClose={() => setModalVer(false)}
        emergencia={emergenciaSeleccionada}
      />

      <ModalRegistrarEjecucion
        isOpen={modalEjecucion}
        onClose={() => setModalEjecucion(false)}
        emergencia={emergenciaSeleccionada}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

// Componente de Badge de Estado
const EstadoBadge = ({ estado }) => {
  const statusConfig = {
    'Por Atender': {
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-200',
      icon: Clock
    },
    'En Atención': {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
      icon: Activity
    },
    'Atendido': {
      bg: 'bg-green-100',
      text: 'text-green-700',
      border: 'border-green-200',
      icon: CheckCircle
    }
  };

  const config = statusConfig[estado] || statusConfig['Por Atender'];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
      <Icon className="w-4 h-4" />
      {estado}
    </span>
  );
};

// Componente de Acciones - DISEÑO SUAVE Y ELEGANTE
const AccionesEmergencia = ({ emergencia, onCambiarEstado, onRegistrarEjecucion, onVer, onEditar }) => {
  const getProximoPaso = () => {
    switch (emergencia.Estado) {
      case 'Por Atender':
        return {
          accion: {
            id: 'en-atencion',
            icon: PlayCircle,
            label: 'Iniciar Atención',
            description: 'Cambiar el estado de esta emergencia a "En Atención"',
            bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
            borderColor: 'border-blue-200',
            textColor: 'text-blue-700',
            iconBg: 'bg-blue-100',
            hoverBg: 'hover:from-blue-100 hover:to-indigo-100',
            hoverBorder: 'hover:border-blue-300',
            onClick: () => onCambiarEstado(emergencia, 'En Atención')
          }
        };
      case 'En Atención':
        return {
          accion: {
            id: 'registrar-ejecucion',
            icon: CheckCircle,
            label: 'Registrar Ejecución',
            description: 'Completar esta emergencia y registrar los trabajos realizados',
            bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50',
            borderColor: 'border-emerald-200',
            textColor: 'text-emerald-700',
            iconBg: 'bg-emerald-100',
            hoverBg: 'hover:from-emerald-100 hover:to-teal-100',
            hoverBorder: 'hover:border-emerald-300',
            onClick: () => onRegistrarEjecucion(emergencia)
          }
        };
      case 'Atendido':
        return { accion: null };
      default:
        return { accion: null };
    }
  };

  const proximoPaso = getProximoPaso();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* SECCIÓN: SIGUIENTE PASO */}
      {proximoPaso.accion && (
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-[#c8a88c] to-[#b8987c] shadow-sm">
              <ArrowRight className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Siguiente Acción</h3>
              <p className="text-xs text-gray-500">Recomendada para esta emergencia</p>
            </div>
          </div>
          
          <button
            onClick={proximoPaso.accion.onClick}
            className={`
              w-full group relative overflow-hidden
              ${proximoPaso.accion.bgColor}
              border-2 ${proximoPaso.accion.borderColor}
              rounded-2xl shadow-md hover:shadow-xl
              transform hover:scale-[1.01] active:scale-[0.99]
              transition-all duration-300
              ${proximoPaso.accion.hoverBg}
              ${proximoPaso.accion.hoverBorder}
            `}
          >
            <div className="relative p-6 flex items-center gap-5">
              {/* Icono */}
              <div className={`flex-shrink-0 p-4 ${proximoPaso.accion.iconBg} rounded-xl backdrop-blur-sm group-hover:scale-110 transition-transform duration-300`}>
                <proximoPaso.accion.icon className={`w-10 h-10 ${proximoPaso.accion.textColor}`} />
              </div>
              
              {/* Contenido */}
              <div className="flex-1 text-left">
                <div className={`text-2xl font-bold ${proximoPaso.accion.textColor} mb-2 flex items-center gap-2`}>
                  {proximoPaso.accion.label}
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {proximoPaso.accion.description}
                </p>
              </div>

              {/* Indicador visual */}
              <div className="hidden md:flex items-center justify-center">
                <div className={`w-12 h-12 rounded-full ${proximoPaso.accion.iconBg} flex items-center justify-center group-hover:scale-110 transition-transform shadow-sm`}>
                  <ArrowRight className={`w-6 h-6 ${proximoPaso.accion.textColor}`} />
                </div>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Mensaje cuando está atendido */}
      {emergencia.Estado === 'Atendido' && (
        <div className="lg:col-span-2">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-md">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 p-4 bg-emerald-100 rounded-xl">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-emerald-700 mb-1">Emergencia Completada</h3>
                <p className="text-emerald-600">Esta emergencia ya ha sido atendida exitosamente</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SECCIÓN: ACCIONES RÁPIDAS */}
      <div className={proximoPaso.accion ? 'lg:col-span-1' : 'lg:col-span-3'}>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-[#c8a88c] rounded-full"></div>
            <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Acciones Rápidas</h4>
          </div>
          
          <div className={`grid ${proximoPaso.accion ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-4'} gap-3`}>
            {/* Ver Detalles */}
            <button
              onClick={() => onVer(emergencia)}
              className="group relative overflow-hidden bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-[#c8a88c] hover:shadow-md transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#c8a88c]/0 to-[#c8a88c]/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex flex-col items-center gap-3">
                <div className="p-3 bg-gray-50 rounded-xl group-hover:bg-[#c8a88c]/10 transition-colors">
                  <Eye className="w-6 h-6 text-gray-600 group-hover:text-[#c8a88c] transition-colors" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-sm text-gray-900">Ver Detalles</div>
                  <div className="text-xs text-gray-500 mt-1">Información completa</div>
                </div>
              </div>
            </button>

            {/* Editar */}
            <button
              onClick={() => onEditar(emergencia)}
              className="group relative overflow-hidden bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-[#c8a88c] hover:shadow-md transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#c8a88c]/0 to-[#c8a88c]/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative flex flex-col items-center gap-3">
                <div className="p-3 bg-gray-50 rounded-xl group-hover:bg-[#c8a88c]/10 transition-colors">
                  <Edit className="w-6 h-6 text-gray-600 group-hover:text-[#c8a88c] transition-colors" />
                </div>
                <div className="text-center">
                  <div className="font-semibold text-sm text-gray-900">Editar</div>
                  <div className="text-xs text-gray-500 mt-1">Modificar datos</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Emergencias;